import React, { useState, useEffect, useContext } from "react";
import CustomPageLayout from "examples/LayoutContainers/CustomPageLayout";
import MDBox from "components/MDBox";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import MDTypography from "components/MDTypography";
import DataTable from "examples/Tables/DataTable";
import { IconButton, Tooltip } from "@mui/material";
import { Visibility } from "@mui/icons-material";
import moment from "moment";
import DefaultCell from "examples/Tables/components/DefaultCell";
import { mockAuditLogs } from "data/mockAuditLogs";
import DraggableDialog from "components/draggagle-dialog";
import ViewAuditLogDetails from "components/AuditLogs/ViewAuditLogDetails";
import { getAuditLogDetails, getAuditLogs } from "lib/Api/AuditLog";
import { LoginFormContext } from "context/loginFormCtx";
import { TABLE_FILTERS } from "utils/constants";
import { getFiltersFromCache } from "utils/cacheManager";
import { getSuperAdminUsers } from "lib/Api/SuperAdminUsers";

const MODULES = [
  { label: "Authentication", value: "Authentication" },
  { label: "Tenant", value: "Tenant" },
  { label: "Roles", value: "Roles" },
  { label: "Service Types", value: "Service Types" },
  { label: "Form Builder", value: "Form Builder" },
  { label: "Plans", value: "Plans" },
  { label: "Coupons", value: "Coupons" },
  { label: "Master Settings", value: "Master Settings" },
  { label: "Profile", value: "Profile" },
  { label: "Email Templates", value: "Email Templates" },
  { label: "Change Password", value: "Change Password" },
  { label: "Super Admin", value: "Super Admin" },
];

function AuditLogs() {
  const [isLoading, setIsLoading] = useState(false);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [filters, setFilters] = useState([]);
  const [sortBy, setSortBy] = useState(
    getFiltersFromCache(TABLE_FILTERS.AUDIT_LOGS) || [{ id: "created_at", desc: true }]
  );
  const [rowCount, setRowCount] = useState(0);
  const { setNotification } = useContext(LoginFormContext);
  const [totalPages, setTotalPages] = useState(0);
  const [viewData, setViewData] = useState(null);

  const columns = [
    // {
    //   Header: "User",
    //   accessor: "user_name",
    //   // filterInputType: "text",
    //   sort: false,
    //   filterInputType: undefined,
    //   Cell: ({ row }) => {
    //     return (
    //       <DefaultCell
    //         value={`${row?.original?.user?.first_name || ""} ${
    //           row?.original?.user?.last_name || ""
    //         }`}
    //       />
    //     );
    //   },
    // },
    {
      Header: "Action",
      accessor: "action",
      filterInputType: "select",
      sort: true,
      filterOptions: [
        { label: "CREATE", value: "CREATE" },
        { label: "DELETE", value: "DELETE" },
        { label: "REASSIGN", value: "REASSIGN" },
        { label: "UPDATE", value: "UPDATE" },
      ],
      Cell: ({ value }) => <DefaultCell value={value} />,
    },
    {
      Header: "Module",
      accessor: "module",
      filterInputType: "select",
      sort: true,
      filterOptions: MODULES?.sort((a, b) => a.label.localeCompare(b.label)),
      Cell: ({ value }) => <DefaultCell value={value} />,
    },
    {
      Header: "IP Address",
      accessor: "ip_address",
      filterInputType: "text",
      sort: true,
      Cell: ({ value }) => <DefaultCell value={value} />,
    },
    {
      Header: "User",
      accessor: "user_id",
      filterInputType: "select",
      filterOptions: [],
      sort: true,
      Cell: ({ row }) => (
        <DefaultCell
          value={`${row?.original?.user?.first_name || ""} ${row?.original?.user?.last_name || ""}`}
        />
      ),
    },
    {
      Header: "Log Date",
      accessor: "created_at",
      filterInputType: "date",
      sort: true,
      Cell: ({ value }) => <DefaultCell value={moment(value).format("MM-DD-YYYY HH:mm:ss")} />,
    },
    {
      Header: "Actions",
      accessor: "actions",
      sort: false,
      Cell: ({ row }) => (
        <MDBox>
          <Tooltip title="View Details" placement="top">
            <IconButton onClick={() => handleViewLog(row.original)}>
              <Visibility />
            </IconButton>
          </Tooltip>
        </MDBox>
      ),
    },
  ];

  const [tableData, setTableData] = useState({
    columns: columns,
    rows: [],
  });

  const handleViewLog = (log) => {
    getAuditLogDetailsApi(log?.id);
    setOpenViewDialog(true);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setPage(0);
  };

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
    setPage(0);
  };

  const handleSortByChange = (newSortBy) => {
    setSortBy(newSortBy);
  };

  const handleClearAllFilters = () => {
    setFilters([]);
    setPage(0);
  };

  const handleGetSuperAdmins = async () => {
    try {
      const tempColumns = [...columns];
      const columnIndex = tempColumns?.findIndex((obj) => obj?.accessor === "user_id");
      
      if(columnIndex !== -1) {
        const response = await getSuperAdminUsers({
          sortBy: [{ id: "first_name", value: "asc" }],
        });

        if (response?.data?.data?.rows) {
          // Create the filter options from the response
          const filterOptions = response.data.data.rows
            .map((user) => ({
              label: `${user?.first_name || ""} ${user?.last_name || ""}`,
              value: user?.id,
            }))
            .sort((a, b) => a.label.localeCompare(b.label));
          
          // Update the column with the new filter options
          tempColumns[columnIndex].filterOptions = filterOptions;
          
          // Force a new reference to trigger re-render
          const newColumns = [...tempColumns];
          
          // Update the table data with the new columns
          setTableData(prevState => ({
            ...prevState,
            columns: newColumns
          }));
        }
      }
    } catch (error) {}
  };

  useEffect(() => {
    handleGetSuperAdmins();
  }, []);
  
  // Re-fetch super admins when columns change
  // useEffect(() => {
  //   const columnIndex = columns?.findIndex((obj) => obj?.accessor === "user_id");
  //   if (columnIndex !== -1 && (!columns[columnIndex].filterOptions || columns[columnIndex].filterOptions.length === 0)) {
  //     handleGetSuperAdmins();
  //   }
  // }, [columns]);

  const getAuditLogDetailsApi = async (id) => {
    try {
      const response = await getAuditLogDetails(id);

      if (response.status === 200) {
        setViewData(response.data.data);
      }
    } catch (error) {
      setNotification({
        color: "error",
        icon: "error",
        title: "Error",
        content: error.message || "Something went wrong!",
        open: true,
      });
    }
  };

  // Simulating API call with mock data
  const getAuditLogData = async () => {
    setIsLoading(true);

    try {
      setIsLoading(true);

      const sortId = sortBy[0]?.id;
      const sortVal = sortBy[0]?.desc ? "desc" : "asc";

      const updatedFilters = filters.map((filter) => {
        if (["action", "module", "user_id"].includes(filter.id)) {
          return {
            ...filter,
            value: filter.value.value,
          };
        }
        return filter;
      });

      const filterBody = {
        page: page + 1,
        pageSize,
        filters: updatedFilters,
        sortBy: sortId ? [{ id: sortId, value: sortVal }] : [],
      };

      const response = await getAuditLogs(filterBody);

      if (response?.status === 200 && response.data?.data) {
        const { rows, count } = response.data.data;

        setTableData((prevState) => ({
          ...prevState,
          rows: rows || [],
        }));
        setRowCount(count || 0);
        setTotalPages(Math.ceil((count || 0) / pageSize));
      } else {
        setNotification({
          color: "error",
          icon: "error",
          title: "Error",
          content: response.data.message,
          open: true,
        });
      }
    } catch (error) {
      setNotification({
        color: "error",
        icon: "error",
        title: "Error",
        content: error.message || "Something went wrong!",
        open: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getAuditLogData();
  }, [page, pageSize, filters, sortBy]);

  return (
    <CustomPageLayout>
      <MDBox pt={3} pb={3}>
        <Card>
          <MDBox p={3} lineHeight={1}>
            <Grid container justifyContent="space-between" spacing={3}>
              <Grid item>
                <MDTypography variant="h5" fontWeight="medium">
                  Audit Logs
                </MDTypography>
                <MDTypography variant="button" color="text">
                  Track all your activities.
                </MDTypography>
              </Grid>
            </Grid>
          </MDBox>

          <DataTable
            isLoading={isLoading}
            table={tableData}
            pageSize={pageSize}
            currentPage={page}
            totalPages={Math.ceil(rowCount / pageSize)}
            handlePageChange={handlePageChange}
            handlePageSizeChange={handlePageSizeChange}
            filters={filters}
            handleFiltersChange={handleFiltersChange}
            sorting={sortBy}
            handleSortByChange={handleSortByChange}
            handleClearAllFilters={handleClearAllFilters}
            rowCount={rowCount}
          />
        </Card>
      </MDBox>

      <DraggableDialog
        dialogTitle="Audit Log Details"
        open={openViewDialog}
        onClose={() => {
          setOpenViewDialog(false);
          setViewData(null);
        }}
        dialogContent={<ViewAuditLogDetails logData={viewData} />}
        maxWidth="xl"
      />
    </CustomPageLayout>
  );
}

export default AuditLogs;
